// Date formatters shared by the three Timeline Lane table components
// (Timeline Lane 1.0.0). Three families, one per schema:
//
//   - events-v1          → BCE-aware (`-2686-01-01` → `2686 BCE`) — deep-time
//   - audit-timeline-v1  → plain ISO with second precision, rendered UTC
//   - timeline-marker-v1 → dynamic-aware (`dynamic:today` → literal
//                          "Today (dynamic)") — never parsed as a Date
//
// All formatters accept `unknown` and coerce defensively: js-yaml MAY
// resolve ISO-shaped scalars to JS Date objects depending on quoting
// (see BookmarksTable's normalizeBookmark), and BCE dates can surface
// as strings or as Dates with negative years. Nothing here ever
// renders "Invalid Date" — unparseable input falls back to String(v).

export const DYNAMIC_TODAY = 'dynamic:today';

/** Coerce a js-yaml scalar (string | Date | other) to a string form.
 *  Dates come back as full ISO; everything else via String(). */
export function coerceDateString(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? '' : v.toISOString();
  }
  return String(v);
}

// ── events-v1: BCE-aware ─────────────────────────────────────────────

/** Parse the (possibly negative) year off an ISO-ish date string.
 *  `-2686-01-01` → -2686; `1969-07-20` → 1969. Returns null when no
 *  leading year is found. Used for display AND chronological sort
 *  (numeric year: negative < positive). */
export function parseYear(v: unknown): number | null {
  const s = coerceDateString(v);
  const m = s.match(/^(-?)(\d{1,6})(?:-|$|T)/);
  if (!m) return null;
  const year = Number(m[2]);
  if (Number.isNaN(year)) return null;
  return m[1] === '-' ? -year : year;
}

/** BCE-aware single-date formatter for events-v1.
 *  `-2686-01-01` → `2686 BCE` (month/day suppressed for the Jan-1
 *  year-placeholder convention); `-0490-09-12` → `490-09-12 BCE`;
 *  `1969-07-20` → `1969-07-20`; `1901-01-01` → `1901-01-01` (CE keeps
 *  the full date — only BCE years drop the placeholder). */
export function formatBceDate(v: unknown): string {
  const s = coerceDateString(v);
  if (!s) return '';
  const m = s.match(/^(-?)(\d{1,6})(?:-(\d{2})-(\d{2}))?/);
  if (!m) return s;
  const [, neg, year, month, day] = m;
  const yearNum = String(Number(year)); // strip zero-padding for display
  if (neg === '-') {
    if (month && day && !(month === '01' && day === '01')) {
      return `${yearNum}-${month}-${day} BCE`;
    }
    return `${yearNum} BCE`;
  }
  if (month && day) return `${yearNum.padStart(4, '0')}-${month}-${day}`;
  return yearNum;
}

/** Span column: `<start> – <end>` BCE-aware when endDate is present,
 *  em-dash when absent. */
export function formatBceSpan(date: unknown, endDate: unknown): string {
  const end = coerceDateString(endDate);
  if (!end) return '—';
  return `${formatBceDate(date)} – ${formatBceDate(end)}`;
}

// ── audit-timeline-v1: second-precision UTC ──────────────────────────

/** `2026-06-04T21:27:02Z` → `2026-06-04 21:27:02 UTC`. Always CE, no
 *  BCE handling. Rendered from the parsed Date's UTC fields so numeric
 *  offsets normalize; strings that don't parse fall back verbatim. */
export function formatAuditTimestamp(v: unknown): string {
  const s = coerceDateString(v);
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)} UTC`;
}

/** Millisecond timestamp for chronological sort of audit rows.
 *  Unparseable → null (sorts last). */
export function parseAuditMillis(v: unknown): number | null {
  const s = coerceDateString(v);
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
}

// ── timeline-marker-v1: dynamic-aware ────────────────────────────────

/** Marker "When" formatter. `dynamic:today` is a sentinel STRING —
 *  render the literal "Today (dynamic)", never parse. Real ISO values
 *  render date-only or date+time depending on precision. */
export function formatMarkerDate(v: unknown): string {
  const s = coerceDateString(v);
  if (!s) return '';
  if (s === DYNAMIC_TODAY) return 'Today (dynamic)';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const iso = d.toISOString();
  // Date-only inputs (YYYY-MM-DD) stay date-only; timestamps keep time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)} UTC`;
}

/** Marker "When" for windows: `<date> – <endDate>`; single dates fall
 *  through to formatMarkerDate. */
export function formatMarkerWhen(date: unknown, endDate: unknown): string {
  const end = coerceDateString(endDate);
  if (!end) return formatMarkerDate(date);
  return `${formatMarkerDate(date)} – ${formatMarkerDate(end)}`;
}
